import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import { ChevronLeft, Mail, RefreshCw, UserPlus, X, Archive, ArchiveRestore } from "lucide-react";
import { api } from "../lib/api";
import { Page, Spinner, Chip } from "../components/Brand";
import { ROLE_LABEL } from "../lib/fr";

const ROLES = [
  { v: "im", l: "Médecine Interne", d: "Crée les références" },
  { v: "obgyn", l: "OB-GYN", d: "Programme les rendez-vous" },
  { v: "admin", l: "Administrateur", d: "Métriques & utilisateurs" },
];

export default function AdminUsers() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const { data: users, isLoading } = useQuery({
    queryKey: ["admin-users", showArchived],
    queryFn: async () =>
      (await api.get(`/admin/users${showArchived ? "?include_archived=true" : ""}`)).data,
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "im", title: "", service: "", notification_email: "" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", service: "", active: true });

  async function invite(e) {
    e.preventDefault();
    if (!form.email || !form.name) return toast.error("Email et nom requis");
    setBusy(true);
    try {
      await api.post("/admin/invitations", {
        ...form, notification_email: form.notification_email || null,
      });
      toast.success("Invitation envoyée");
      setOpen(false);
      setForm({ email: "", name: "", role: "im", title: "", service: "", notification_email: "" });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  }

  async function resend(uid) {
    try {
      await api.post(`/admin/users/${uid}/resend-invitation`);
      toast.success("Invitation renvoyée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  }

  async function toggleActive(u) {
    const action = u.active ? "désactiver" : "activer";
    if (!window.confirm(`Voulez-vous vraiment ${action} le compte de ${u.name} ?`)) return;
    try {
      await api.patch(`/admin/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Compte désactivé" : "Compte activé");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  }

  async function archive(u) {
    if (!window.confirm(`Archiver ${u.name} ? Le compte sera désactivé et masqué par défaut.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/archive`);
      toast.success("Utilisateur archivé");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  }

  async function unarchive(u) {
    try {
      await api.post(`/admin/users/${u.id}/unarchive`);
      toast.success("Utilisateur restauré");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  }

  function openEdit(u) {
    setEditing(u);
    setEditForm({ title: u.title || "", service: u.service || "", active: !!u.active });
  }
  async function saveEdit() {
    try {
      await api.patch(`/admin/users/${editing.id}`, editForm);
      toast.success("Utilisateur mis à jour");
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  }

  return (
    <Page>
      <Toaster position="top-center" richColors />
      <button onClick={() => navigate("/admin")} className="text-[14px] mb-4 inline-flex items-center gap-1 hover:underline"
              style={{ color: "var(--cs-text-muted)" }} data-testid="back-link">
        <ChevronLeft size={16} /> Tableau de bord
      </button>

      <div className="flex items-end justify-between mb-6 md:mb-8 flex-wrap gap-3">
        <div>
          <div className="cs-label">Administration</div>
          <h1 className="font-semibold mt-1" style={{ fontSize: 30, letterSpacing: "-0.6px" }}>Utilisateurs</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowArchived((v) => !v)}
                  className={showArchived ? "cs-btn-primary text-[13px]" : "cs-btn-ghost text-[13px]"}
                  data-testid="toggle-archived-users">
            <Archive size={15} /> {showArchived ? "Masquer archivés" : "Afficher archivés"}
          </button>
          <button onClick={() => setOpen(true)} className="cs-btn-primary" data-testid="invite-button">
            <UserPlus size={18} /> Inviter un médecin
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="cs-card p-10 flex justify-center"><Spinner /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="users-grid">
          {users.map((u) => (
            <div key={u.id} className="cs-card p-5" style={{ opacity: u.archived ? 0.6 : 1 }} data-testid={`user-card-${u.email}`}>
              <div className="flex items-start justify-between pb-3 border-b" style={{ borderColor: "var(--cs-border)" }}>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[16px]">{u.name}</span>
                    {u.archived && <Chip tone="muted">Archivé</Chip>}
                  </div>
                  <div className="text-[12px] mt-0.5" style={{ color: "var(--cs-text-muted)" }}>{u.email}</div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <Chip tone="primary">{ROLE_LABEL[u.role]}</Chip>
                  {!u.active ? <Chip tone="danger">Inactif</Chip> : <Chip tone="muted">Actif</Chip>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-y-2 pt-3 text-[13px]">
                <div><div className="cs-label">Titre</div><div className="mt-0.5">{u.title || "—"}</div></div>
                <div><div className="cs-label">Service</div><div className="mt-0.5">{u.service || "—"}</div></div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {!u.archived && (
                  <button onClick={() => openEdit(u)} className="cs-btn-ghost text-[13px]" data-testid={`edit-${u.email}`}>
                    Modifier
                  </button>
                )}
                {!u.active && !u.archived && (
                  <button onClick={() => resend(u.id)} className="cs-btn-ghost text-[13px]" data-testid={`resend-${u.email}`}>
                    <RefreshCw size={14} /> Renvoyer l'invitation
                  </button>
                )}
                {!u.archived && (
                  <button onClick={() => toggleActive(u)} className="cs-btn-danger-outline text-[13px]" data-testid={`toggle-${u.email}`}>
                    {u.active ? "Désactiver" : "Activer"}
                  </button>
                )}
                {!u.archived ? (
                  <button onClick={() => archive(u)} className="cs-btn-danger-outline text-[13px]" data-testid={`archive-${u.email}`}>
                    <Archive size={14} /> Archiver
                  </button>
                ) : (
                  <button onClick={() => unarchive(u)} className="cs-btn-ghost text-[13px]" data-testid={`unarchive-${u.email}`}>
                    <ArchiveRestore size={14} /> Restaurer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invite modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4 cs-fade" onClick={() => setOpen(false)}>
          <div className="cs-card w-full max-w-[480px] p-6" onClick={(e) => e.stopPropagation()} data-testid="invite-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-[18px]" style={{ letterSpacing: "-0.3px" }}>Inviter un médecin</h2>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-[var(--cs-bg)] rounded-full" data-testid="close-invite"><X size={18} /></button>
            </div>
            <form onSubmit={invite} className="flex flex-col gap-4">
              <div>
                <div className="cs-label mb-1.5">Email</div>
                <input type="email" className="cs-input" value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" />
              </div>
              <div>
                <div className="cs-label mb-1.5">Nom complet</div>
                <input className="cs-input" value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" />
              </div>
              <div>
                <div className="cs-label mb-2">Rôle</div>
                <div className="grid grid-cols-3 gap-2" data-testid="invite-roles">
                  {ROLES.map((r) => (
                    <button type="button" key={r.v}
                      onClick={() => setForm({ ...form, role: r.v })}
                      className={`cs-picker text-left ${form.role === r.v ? "active" : ""}`}
                      data-testid={`invite-role-${r.v}`}>
                      <div className="title">{r.l}</div>
                      <div className="desc">{r.d}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="cs-label mb-1.5">Titre</div>
                  <input className="cs-input" placeholder="ex: Cardiologue" value={form.title}
                         onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="invite-title" />
                </div>
                <div>
                  <div className="cs-label mb-1.5">Service</div>
                  <input className="cs-input" placeholder="ex: Médecine Interne" value={form.service}
                         onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="invite-service" />
                </div>
              </div>
              <div>
                <div className="cs-label mb-1.5">Email de notification (optionnel)</div>
                <input type="email" className="cs-input" placeholder="Autre email pour les notifications"
                       value={form.notification_email}
                       onChange={(e) => setForm({ ...form, notification_email: e.target.value })} data-testid="invite-notif-email" />
              </div>
              <div className="flex justify-end gap-3 mt-1">
                <button type="button" onClick={() => setOpen(false)} className="cs-btn-ghost">Annuler</button>
                <button type="submit" disabled={busy} className="cs-btn-primary" data-testid="send-invite">
                  {busy ? <Spinner /> : <><Mail size={16} /> Envoyer l'invitation</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4 cs-fade" onClick={() => setEditing(null)}>
          <div className="cs-card w-full max-w-[440px] p-6" onClick={(e) => e.stopPropagation()} data-testid="edit-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-[18px]" style={{ letterSpacing: "-0.3px" }}>Modifier {editing.name}</h2>
              <button onClick={() => setEditing(null)} className="p-1 rounded-full hover:bg-[var(--cs-bg)]"><X size={18} /></button>
            </div>
            <div className="flex flex-col gap-4">
              <div>
                <div className="cs-label mb-1.5">Titre</div>
                <input className="cs-input" value={editForm.title}
                       onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="edit-title" />
              </div>
              <div>
                <div className="cs-label mb-1.5">Service</div>
                <input className="cs-input" value={editForm.service}
                       onChange={(e) => setEditForm({ ...editForm, service: e.target.value })} data-testid="edit-service" />
              </div>
              <div className="flex items-center justify-between">
                <div className="cs-label">Actif</div>
                <button type="button"
                        onClick={() => setEditForm({ ...editForm, active: !editForm.active })}
                        className={`cs-toggle ${editForm.active ? "on" : ""}`} data-testid="edit-active-toggle" />
              </div>
              <div className="flex justify-end gap-3 mt-2">
                <button onClick={() => setEditing(null)} className="cs-btn-ghost">Annuler</button>
                <button onClick={saveEdit} className="cs-btn-primary" data-testid="save-edit">Enregistrer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
